import React from "react";
import Drawer from "./components/ui/Drawer";
import { MenuProvider } from "./context/MenuContext";
import { useAuth } from "./context/AuthContext";
import { useTranslation } from "react-i18next";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import LandingPage from "./pages/LandingPage";

import Login from "./pages/Login";
import ExcursionGate from "./pages/ExcursionGate";
import ExcursionGoto from "./pages/ExcursionGoto";
import Register from "./pages/Register";
import ExcursionJoin from "./pages/ExcursionJoin";
import ExcursionCreate from "./pages/ExcursionCreate";

import AppMenu from "./components/navigation/AppMenu";
import AppMenuSection from "./components/navigation/AppMenuSection";
import AppMenuAction from "./components/navigation/AppMenuAction";

import DashboardRedirect from "./pages/DashboardRedirect";

import ExcursionSummary from "./pages/ExcursionSummary";
import MySummary from "./pages/MySummary";

import CreateABill from "./pages/CreateABill";
import UpdateABill from "./pages/UpdateABill";
import ManageBills from "./pages/ManageBills";
import ManageMembers from "./pages/ManageMembers";
import ManageExcursion from "./pages/ManageExcursion";

import ExcursionTable  from "./pages/ExcursionTable";
import BillsSettlementPlan from "./pages/BillsSettlementPlan";
import SettleMe from "./pages/SettleMe";

import AboutMe from "./pages/AboutMe";
import ChangePassword from "./pages/ChangePassword";

const AUTH_PAGES = [
  "/login",
  "/register",
  "/excursion-gate",
  "/excursion-goto",
  "/excursion-join",
  "/excursion-create",
];

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loadingUser } = useAuth();
  const { i18n, t } = useTranslation(); // ✅ added t

  const [menuOpen, setMenuOpen] = React.useState(false);

  const touchStartX = React.useRef(null);
  const touchCurrentX = React.useRef(null);

  const handleTouchStart = (e) => {
    const x = e.touches[0].clientX;
    if (x < 30) {
      touchStartX.current = x;
    }
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchCurrentX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null) return;

    const delta = touchCurrentX.current - touchStartX.current;
    if (delta > 80) {
      setMenuOpen(true);
    }

    touchStartX.current = null;
    touchCurrentX.current = null;
  };

  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isAuthPage = AUTH_PAGES.includes(location.pathname);
  const isLoggedIn = Boolean(user);

  const goTo = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);
    setMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate("/login", { replace: true });
  };

  return (
    <MenuProvider
      value={{
        openMenu: () => setMenuOpen(true),
        closeMenu: () => setMenuOpen(false),
      }}
    >
      {!loadingUser && !isAuthPage && isLoggedIn && (
        <Drawer open={menuOpen} onClose={() => setMenuOpen(false)}>
          <AppMenu>
            <AppMenuSection title={t("menu.section")}>
              <AppMenuAction onClick={() => goTo("/my-summary")}>
                {t("menu.summary")}
              </AppMenuAction>

              <AppMenuAction onClick={() => goTo("/create-bill")}>{t("menu.create_bill")}</AppMenuAction>
              <AppMenuAction onClick={() => goTo("/update-bill")}>{t("menu.update_bill")}</AppMenuAction>
              <AppMenuAction onClick={() => goTo("/manage-bills")}>{t("menu.manage_bills")}</AppMenuAction>
              <AppMenuAction onClick={() => goTo("/manage-members")}>{t("menu.manage_members")}</AppMenuAction>
              <AppMenuAction onClick={() => goTo("/manage-excursion")}>{t("menu.manage_excursion")}</AppMenuAction>

              <AppMenuAction onClick={() => goTo("/excursion-summary")}>{t("menu.excursion_summary")}</AppMenuAction>
              <AppMenuAction onClick={() => goTo("/excursion-table")}>{t("menu.full_details")}</AppMenuAction>
              <AppMenuAction onClick={() => goTo("/settlement-plan-among-users")}>{t("menu.settlement_plan")}</AppMenuAction>
              <AppMenuAction onClick={() => goTo("/settle-me")}>{t("menu.settle_me")}</AppMenuAction>

              <AppMenuAction onClick={() => goTo("/about-me")}>{t("menu.about_me")}</AppMenuAction>
              <AppMenuAction onClick={() => goTo("/change-password")}>{t("menu.change_password")}</AppMenuAction>

              <AppMenuAction onClick={() => changeLanguage("tr")}>TR</AppMenuAction>
              <AppMenuAction onClick={() => changeLanguage("fr")}>FR</AppMenuAction>
              <AppMenuAction onClick={() => changeLanguage("en")}>EN</AppMenuAction>

              <AppMenuAction variant="danger" onClick={handleLogout}>
                {t("menu.logout")}
              </AppMenuAction>
            </AppMenuSection>
          </AppMenu>
        </Drawer>
      )}

      <div
        style={{ padding: "1rem" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </MenuProvider>
  );
}


function PrivateRoute({ children }) {
  const { user, loadingUser } = useAuth();

  if (loadingUser) {
    return null;
  }

  return user ? children : <Navigate to="/login" replace />;
}

function AppRoutes() {
  const { user, loadingUser } = useAuth();

  if (loadingUser) {
    return null;
  }

  const fallbackPath = user ? "/user" : "/login";

  return (
    <Layout>
      <Routes>
     	  
        <Route path="/" element={<LandingPage />} />
		    <Route path="/landing" element={<LandingPage />} />
        
		    <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        <Route path="/excursion-gate" element={<ExcursionGate />} />
        <Route path="/excursion-goto" element={<ExcursionGoto />} />
        <Route path="/excursion-join" element={<ExcursionJoin />} />
        <Route path="/excursion-create" element={<ExcursionCreate />} />

        <Route path="/my-summary" element={<PrivateRoute><MySummary /></PrivateRoute>} />
        <Route path="/user" element={<PrivateRoute><DashboardRedirect /></PrivateRoute>} />

        <Route path="/create-bill" element={<PrivateRoute><CreateABill /></PrivateRoute>} />
        <Route path="/update-bill" element={<PrivateRoute><UpdateABill /></PrivateRoute>} />

        <Route path="/manage-members" element={<PrivateRoute><ManageMembers /></PrivateRoute>} />
        <Route path="/manage-bills" element={<PrivateRoute><ManageBills /></PrivateRoute>} />
        <Route path="/manage-excursion" element={<PrivateRoute><ManageExcursion /></PrivateRoute>} />

        <Route path="/excursion-table" element={<PrivateRoute><ExcursionTable /></PrivateRoute>} />
        <Route path="/settlement-plan-among-users" element={<PrivateRoute><BillsSettlementPlan /></PrivateRoute>} />

        <Route path="/settle-me" element={<PrivateRoute><SettleMe /></PrivateRoute>} />

        <Route path="/excursion-summary" element={<PrivateRoute><ExcursionSummary /></PrivateRoute>} />
        <Route path="/about-me" element={<PrivateRoute><AboutMe /></PrivateRoute>} />

        <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />

        <Route path="*" element={<Navigate to={fallbackPath} replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;